import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Modal,
  Button,
  Checkbox,
  Toggle,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ButtonGroup,
} from '@statsbomb/kitbag-components';
import { useReactiveVar } from '@apollo/client';
import uiDefault from '../../apollo/uiDefault';
import {
  uiState,
  cf_FormationsEnabled,
  cf_FormationsOffense,
} from '../../apollo';

import { OFFENSE_FORMATIONS } from '../../utils/constants/charting';
import {
  TriggerButtonContainer,
  SelectAllButtonContainer,
  FilterRoundel,
  ModalWidth,
  FormationsLayout,
} from './ContextFilters.styles';
import { enableFilter } from './Filters.helper';
import { useIsEligiblePage } from './ContextFilters.hooks';
import { FormField, FormGroupEqual } from '../../styles/global.styles';

const FormationFilter = ({ handleEnabledToggle }) => {
  // modal open/close
  const [st_ModalOpen, setModalOpen] = useState(false);
  // context filters in global state
  const cf_FormationsOffenseRV = useReactiveVar(cf_FormationsOffense);
  const cf_FormationsEnabledRV = useReactiveVar(cf_FormationsEnabled);

  // context filters in local state
  const [st_FormationsOffense, setFormationsOffense] = useState(
    cf_FormationsOffenseRV
  );

  const handleOpen = () => {
    setModalOpen(true);
  };

  const handleFormationsOffense = (isChecked, index) => {
    const formationsOffense = [...st_FormationsOffense];
    formationsOffense[index] = isChecked;
    setFormationsOffense([...formationsOffense]);
    enableFilter(cf_FormationsEnabledRV, cf_FormationsEnabled, 'formations');
  };

  const handleAll = (condition) => {
    setFormationsOffense(new Array(OFFENSE_FORMATIONS.length).fill(condition));
    enableFilter(cf_FormationsEnabledRV, cf_FormationsEnabled, 'formations');
  };

  const handleReset = () => {
    const { formations } = uiDefault.contextFilters;

    setFormationsOffense(new Array(OFFENSE_FORMATIONS.length).fill(false));
    cf_FormationsEnabled(formations.enabled);

    uiState().contextFilters.formations.offenseFormations = new Array(
      OFFENSE_FORMATIONS.length
    ).fill(false);
    uiState().contextFilters.formations.enabled = formations.enabled;

    sessionStorage.setItem('uiStateLocal', JSON.stringify(uiState()));
  };

  const handleModalClose = () => {
    const { contextFilters } = uiState();
    const { formations } = contextFilters;

    cf_FormationsOffense([...st_FormationsOffense]);
    formations.offenseFormations = [...st_FormationsOffense];
    cf_FormationsEnabled(cf_FormationsEnabledRV);

    sessionStorage.setItem('uiStateLocal', JSON.stringify(uiState()));
    setModalOpen(false);
  };

  const filterCount = [cf_FormationsEnabledRV].filter(Boolean).length;
  const isEligiblePage = useIsEligiblePage('FORMATIONS');
  const isEnabled = filterCount > 0 && isEligiblePage;

  // slicing on the first value of each formation to display in columns
  const offenseCol1 = [...OFFENSE_FORMATIONS.slice(0, 4)];
  const offenseCol2 = [...OFFENSE_FORMATIONS.slice(4, 8)];
  const offenseCol3 = [...OFFENSE_FORMATIONS.slice(8, 12)];
  const offenseCol4 = [...OFFENSE_FORMATIONS.slice(12, 15)];
  const offenseCol5 = [...OFFENSE_FORMATIONS.slice(15, 17)];
  const offenseCol6 = [...OFFENSE_FORMATIONS.slice(17, 18)];

  return (
    <>
      <TriggerButtonContainer>
        <Button
          variant={isEnabled ? 'primary' : 'secondary'}
          size="small"
          isDisabled={!isEligiblePage}
          onClick={handleOpen}
        >
          Formations
        </Button>
        {isEnabled && <FilterRoundel>{filterCount}</FilterRoundel>}
      </TriggerButtonContainer>

      <Modal onCancel={handleModalClose} isOpen={st_ModalOpen}>
        <ModalWidth $minWidth="570px">
          <ModalHeader>Formations</ModalHeader>
          <ModalBody>
            <FormationsLayout>
              <FormField>
                <Toggle
                  id="formations-cf-toggle"
                  label="Formations"
                  onChange={() =>
                    handleEnabledToggle(
                      !cf_FormationsEnabledRV,
                      cf_FormationsEnabled,
                      'formations',
                      {
                        enabled: !cf_FormationsEnabledRV,
                      }
                    )
                  }
                  checked={cf_FormationsEnabledRV}
                  size="small"
                  data-testid="offense-formations"
                />
              </FormField>

              <div className="half">
                <h3>Offense</h3>
                <FormGroupEqual>
                  <div className="packages">
                    {offenseCol1.map((item, index) => (
                      <div key={`offense-formations-${item}`}>
                        <Checkbox
                          id={`offense-formations-${item}-checkbox`}
                          label={item.substring(2)}
                          onChange={() =>
                            handleFormationsOffense(
                              !st_FormationsOffense[index],
                              index
                            )
                          }
                          checked={st_FormationsOffense[index]}
                          size="small"
                          disabled={!cf_FormationsEnabledRV}
                          data-testid={`offense-formations-${index}`}
                        />
                      </div>
                    ))}
                  </div>
                  <div className="packages">
                    {offenseCol2.map((item, index) => (
                      <div key={`offense-formations${item}`}>
                        <Checkbox
                          id={`offense-formations${item}-checkbox`}
                          label={item.substring(2)}
                          onChange={() =>
                            handleFormationsOffense(
                              !st_FormationsOffense[index + 4],
                              index + 4
                            )
                          }
                          checked={st_FormationsOffense[index + 4]}
                          size="small"
                          disabled={!cf_FormationsEnabledRV}
                          data-testid={`offense-formations-${index + 4}`}
                        />
                      </div>
                    ))}
                  </div>
                  <div className="packages">
                    {offenseCol3.map((item, index) => (
                      <div key={`offense-formations-${item}`}>
                        <Checkbox
                          id={`offense-formations-${item}-checkbox`}
                          label={item.substring(2)}
                          onChange={() =>
                            handleFormationsOffense(
                              !st_FormationsOffense[index + 8],
                              index + 8
                            )
                          }
                          checked={st_FormationsOffense[index + 8]}
                          size="small"
                          disabled={!cf_FormationsEnabledRV}
                          data-testid={`offense-formations-${index + 8}`}
                        />
                      </div>
                    ))}
                  </div>
                  <div className="packages">
                    {offenseCol4.map((item, index) => (
                      <div key={`offense-formations-${item}`}>
                        <Checkbox
                          id={`offense-formations-${item}-checkbox`}
                          label={item.substring(2)}
                          onChange={() =>
                            handleFormationsOffense(
                              !st_FormationsOffense[index + 12],
                              index + 12
                            )
                          }
                          checked={st_FormationsOffense[index + 12]}
                          size="small"
                          disabled={!cf_FormationsEnabledRV}
                          data-testid={`offense-formations-${index + 12}`}
                        />
                      </div>
                    ))}
                  </div>
                  <div className="packages">
                    {offenseCol5.map((item, index) => (
                      <div key={`offense-formations-${item}`}>
                        <Checkbox
                          id={`offense-formations-${item}-checkbox`}
                          label={item.substring(2)}
                          onChange={() =>
                            handleFormationsOffense(
                              !st_FormationsOffense[index + 15],
                              index + 15
                            )
                          }
                          checked={st_FormationsOffense[index + 15]}
                          size="small"
                          disabled={!cf_FormationsEnabledRV}
                          data-testid={`offense-formations-${index + 15}`}
                        />
                      </div>
                    ))}
                  </div>
                  <div className="packages">
                    {offenseCol6.map((item, index) => (
                      <div key={`offense-formations-${item}`}>
                        <Checkbox
                          id={`offense-formations-${item}-checkbox`}
                          label={item.substring(2)}
                          onChange={() =>
                            handleFormationsOffense(
                              !st_FormationsOffense[index + 17],
                              index + 17
                            )
                          }
                          checked={st_FormationsOffense[index + 17]}
                          size="small"
                          disabled={!cf_FormationsEnabledRV}
                          data-testid={`offense-formations-${index + 17}`}
                        />
                      </div>
                    ))}
                  </div>
                </FormGroupEqual>
                <SelectAllButtonContainer>
                  <ButtonGroup>
                    <Button
                      size="small"
                      onClick={() => handleAll(true)}
                      variant="secondary"
                      isDisabled={!cf_FormationsEnabledRV}
                    >
                      Select All
                    </Button>
                    <Button
                      size="small"
                      onClick={() => handleAll(false)}
                      variant="secondary"
                      isDisabled={!cf_FormationsEnabledRV}
                    >
                      Deselect All
                    </Button>
                  </ButtonGroup>
                </SelectAllButtonContainer>
              </div>
            </FormationsLayout>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={handleReset}>
              Reset
            </Button>
            <Button onClick={handleModalClose}>Save</Button>
          </ModalFooter>
        </ModalWidth>
      </Modal>
    </>
  );
};

FormationFilter.propTypes = {
  handleEnabledToggle: PropTypes.func.isRequired,
};

export default FormationFilter;
